import { useContext, useEffect, useState } from 'react'

import Grid from '@mui/material/Grid2'

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'

import dayjs, { type Dayjs } from '../../../utils/date'

import { FormControl, FormHelperText } from '@mui/material'
import { first, isEmpty } from 'lodash'
import { ChangeSourceEnum, FormField, updateExperimentFormField } from '../../../api'
import { AppStateContext } from '../../../state/AppProvider'
import { ToastMessage } from '../../ExperimentForm/ExperimentFormToast'
import { FormFieldLabel } from '../FormFieldLabel'
import styles from './DateFormField.module.css'

interface Props {
  name: string
  stepName: string
  fieldName: string
  convId: string
  gridSize: number
  data: FormField<string>
  tooltipInfo?: string
  onError?: (message: Pick<ToastMessage, 'title' | 'description' | 'type'>) => void
  disabled?: boolean
}

export const DateFormField = ({
  name,
  stepName,
  fieldName,
  convId,
  gridSize,
  data,
  tooltipInfo,
  onError,
  disabled,
}: Props) => {
  const appStateContext = useContext(AppStateContext)
  const [value, setValue] = useState(data?.field_value ? dayjs.utc(data?.field_value) : null)

  async function handleChange(newValue: Dayjs | null) {
    try {
      const newConversation = await updateExperimentFormField(
        stepName,
        fieldName,
        newValue?.toISOString() || '',
        convId,
        appStateContext?.state.shouldValidateForm ?? false,
        appStateContext?.state.frontendSettings || undefined,
      )

      if (newConversation) {
        const messages = appStateContext?.state.currentChat?.messages || []
        newConversation.messages = messages

        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: newConversation })
      }
    } catch (error) {
      const errorWithReactElement = error as Error & { reactElement?: React.ReactNode }
      onError?.({
        title: 'Error',
        description:
          errorWithReactElement?.reactElement ||
          (error instanceof Error ? error.message : 'An error occurred while updating the field'),
        type: 'error',
      })
    }
  }

  useEffect(() => {
    setValue(data?.field_value ? dayjs.utc(data?.field_value) : null)
  }, [data?.field_value])

  return (
    <Grid
      size={gridSize}
      id={`field_${fieldName}`}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <FormFieldLabel
          text={name}
          isAiGenerated={data?.change_source !== ChangeSourceEnum.MANUAL}
          tooltipInfo={tooltipInfo}
        />
        <FormControl
          error={!isEmpty(data.errors)}
          fullWidth>
          <DatePicker
            className={styles.dateFormField}
            value={value}
            onChange={handleChange}
            disabled={disabled}
            slotProps={{
              day: { classes: { selected: styles.selectedDay } },
              yearButton: { className: styles.yearButton },
              textField: {
                className: isEmpty(data.errors) ? (disabled ? styles.disabledDateField : undefined) : styles.errorInput,
                error: !isEmpty(data.errors),
              },
            }}
          />
          <FormHelperText>{first(data.errors)}</FormHelperText>
        </FormControl>
      </LocalizationProvider>
    </Grid>
  )
}
